<script context="module">
  import gql from 'graphql-tag';
  import { client } from 'utils/apollo.js';
  import determineProductColourImageUrl from '../../utils/determineProductColourImageUrl.js';

  const PRODUCT = gql`
    query ($_eq: String!) {
      Product_ProductCollections(where: { slug: { _eq: $_eq } }, limit: 1) {
        id
        name
        slug
        metaDescription
        description
        Product {
          id
          slug
          price
          details
          displayColourPath
          useCoverImageForPreview
          isModelBasedImages
          sceneImages
          reversible
          mensAvailable
          womensAvailable
          youthAvailable
          isSublimationProduct
          printPositions
          Product_Sizes(order_by: { sizeId: asc }) {
            id
            width
            length
            units
            useAlternateSystem
            ProductSize {
              id
              name
              gender
            }
          }
          ProductType {
            id
            name
          }
          Product_Colours(where: { visible: { _eq: true } }) {
            displayName
            mens
            womens
            youth
            ProductColoursMasterMain {
              rgb
            }
            ProductColoursMasterTrim1 {
              rgb
            }
            ProductColoursMasterTrim2 {
              rgb
            }
            ProductColoursMasterTrim3 {
              rgb
            }
            pathName
          }
          Product_SublimationTemplates(where: { isVisible: { _eq: true } }) {
            id
            isDefault
            isVisible
            SublimationTemplate {
              id
              name
              numberOfColours
            }
          }
          BulkPricingDiscounts(order_by: { minQty: asc }) {
            id
            minQty
            maxQty
            discountPercent
          }
          Supplier {
            id
            displayName
          }
        }
        ProductCollection {
          id
          name
          slug
          ProductCategory {
            id
            name
            slug
          }
        }
      }
    }
  `;

  export async function preload(page) {
    const { gender, colour } = page.query;

    const { slug } = page.params;
    let response = await client.query({
      query: PRODUCT,
      variables: { _eq: slug },
    });

    const product = response.data.Product_ProductCollections[0];
    if (!product) {
      return { product: null }; // or throw an error / redirect
    }

    let displayColourObject = product.Product.Product_Colours.find(
      (c) => c.pathName === product.Product.displayColourPath,
    );
    if (!displayColourObject) {
      // fallback to first colour if the displayed colour path is missing
      displayColourObject = product.Product.Product_Colours[0];
    }

    // Prebuild your JSON-LD images & OG images arrays right here:
    let jsonLDImages = [];
    let ogImages = [];

    // Helper function to get Sublimation Template ID if needed
    const getSublimationTemplateId = () => {
      if (!product.Product.isSublimationProduct) return undefined;
      // find default template, or fallback to first
      let defaultTemplate = product.Product.Product_SublimationTemplates.find((x) => x.isDefault)?.SublimationTemplate;
      if (!defaultTemplate && product.Product.Product_SublimationTemplates[0]) {
        defaultTemplate = product.Product.Product_SublimationTemplates[0].SublimationTemplate;
      }
      return defaultTemplate?.id;
    };

    // For each gender that’s actually available, generate the URL
    async function addGenderImage(gender) {
      const url = await determineProductColourImageUrl({
        productSlug: product.Product.slug,
        colourPathName: product.Product.displayColourPath,
        colourObject: displayColourObject,
        imageSize: '512',
        imageType: 'jpg',
        side: 'front',
        showModel: true,
        gender,
        isModelBasedImages: product.Product.isModelBasedImages,
        sublimationTemplateId: getSublimationTemplateId(),
      });
      jsonLDImages.push(url);
      ogImages.push({
        url,
        width: 512,
        height: 625,
        alt: `front image of a model wearing ${product.name} in ${displayColourObject.displayName}`,
      });
    }

    // Conditionally generate mens/womens/youth if available
    if (product.Product.mensAvailable) await addGenderImage('mens');
    if (product.Product.womensAvailable) await addGenderImage('womens');
    if (product.Product.youthAvailable) await addGenderImage('youth');

    // Scene images from DB
    let sceneImages = JSON.parse(product.Product.sceneImages || '[]');
    for (let i = 0; i < sceneImages.length; i++) {
      if (sceneImages[i]?.jpg?.w512) {
        jsonLDImages.push(sceneImages[i].jpg.w512);
        ogImages.push({
          url: sceneImages[i].jpg.w512,
          width: 512,
          alt: sceneImages[i]?.altText,
        });
      }
    }

    // Return everything you need for SSR
    return {
      product,
      jsonLDImages,
      ogImages,
      gender,
      colour,
    };
  }
</script>

<script>
  // TODO - expand jsonld to include all product data and reviews, etc.
  import { MetaTags, JsonLd } from 'svelte-meta-tags';
  import { onMount, getContext } from 'svelte';

  import { capitalize } from '../../utils/capitalizeString.js';
  import { determineProductFromPrice } from '../../utils/pricing.js';

  import Breadcrumb from '../../components/Breadcrumb.svelte';
  import Faqs from '../../components/Faqs.svelte';
  import TrustedBy from '../../components/TrustedBy.svelte';
  // import Coordinating from "../../layouts/single-product/Coordinating.svelte";
  import ProductDetail from '../../layouts/single-product/ProductDetail.svelte';
  import HowItWorks from '../../components/HowItWorks.svelte';
  import FullWidthCta from '../../components/FullWidthCTA.svelte';
  import SublimationTemplateGrid from '../../components/SublimationTemplateGrid.svelte';
  // import BenefitsV2 from '../../components/LandingPage/BenefitsV2.svelte';
  // import Recent from "../../layouts/single-product/Recent.svelte";
  // import Reviews from "../../layouts/single-product/Reviews.svelte";
  // import Youmaylike from "../../layouts/single-product/Youmaylike.svelte";

  export let product;
  export let jsonLDImages;
  export let ogImages;
  export let gender;
  export let colour;

  const links = [
    { name: 'Home', slug: '/' },
    { name: 'Products', slug: 'products/' },
    {
      name: product.name,
      slug: 'product/' + product.slug,
    },
  ];

  let pageTitle = capitalize(product.name);

  const defaultBulkDiscounts = getContext('defaultBulkDiscounts');
  const productSpecificBulkDiscounts = product.Product.BulkPricingDiscounts;

  const displayPrice = determineProductFromPrice(product.price, productSpecificBulkDiscounts, defaultBulkDiscounts);

  onMount(() => {
    // Send view_item event to Google Analytics
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ ecommerce: null });
    window.dataLayer.push({
      event: 'view_item',
      ecommerce: {
        items: [
          {
            index: 0,
            name: product.name,
            price: (product.Product.price / 100).toFixed(2),
            quantity: 1,
            currency: 'AUD',
            item_category: product.ProductCollection.ProductCategory.name,
            item_category2: product.ProductCollection.name,
          },
        ],
      },
    });

    // Send view_item event to Facebook Pixel
    if (typeof fbq !== 'undefined') {
      fbq('track', 'ViewContent', {
        content_name: product.name,
        content_category: product.ProductCollection.ProductCategory.name,
        content_ids: [product.id],
        content_type: 'product',
        value: (product.Product.price / 100).toFixed(2),
        currency: 'AUD',
      });
    }
  });
</script>

<MetaTags
  title={pageTitle}
  titleTemplate="%s | Uniform Hero"
  description={product.metaDescription}
  canonical="https://uniformhero.com.au/product/{product.slug}"
  openGraph={{
    url: `https://uniformhero.com.au/product/${product.slug}`,
    title: `${pageTitle} | Uniform Hero`,
    description: `${product.metaDescription}`,
    images: ogImages,
    site_name: 'Uniform Hero',
    type: 'website',
  }}
/>

<JsonLd
  schema={{
    '@type': 'Product',
    name: product.name,
    image: jsonLDImages,
    description: product.description,
    offers: {
      '@type': 'Offer',
      url: `https://uniformhero.com.au/product/${product.slug}`,
      priceCurrency: 'AUD',
      price: displayPrice,
      itemCondition: 'https://schema.org/NewCondition',
      availability: 'https://schema.org/InStock',
    },
  }}
/>

<Breadcrumb {links} />
<ProductDetail
  sizes={product.Product.Product_Sizes}
  details={product}
  selectedGender={gender}
  colourPathName={colour}
/>
<!-- <Coordinating /> -->
<!-- <Youmaylike /> -->
<!-- <Reviews /> -->
<!-- <Recent /> -->
<HowItWorks />
{#if product.Product.isSublimationProduct}
  <SublimationTemplateGrid productDetails={product} />
{/if}

<FullWidthCta
  path={`/design/${product.slug}/${product.Product.isSublimationProduct ? 'choose-template' : 'choose-colours'}`}
/>
<Faqs />
<TrustedBy showDesignButton={false} />
<FullWidthCta
  path={`/design/${product.slug}/${product.Product.isSublimationProduct ? 'choose-template' : 'choose-colours'}`}
/>
